import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { useToast } from '@radiology/ui';
import dayjs from 'dayjs';
import { useReviewCertificate, type Certificate } from './api';

/**
 * Approve or reject one certificate.
 *
 * The rules live in the database: a rejection must carry a comment, a
 * certificate can only be reviewed once, and approval stamps the date. This
 * dialog mirrors the comment requirement so the user finds out before
 * submitting, but the trigger is what enforces it — if the two ever disagree,
 * the database wins and its message is shown.
 */
export function ReviewDialog({
  certificate,
  reviewerName,
  onClose,
}: {
  certificate: Certificate | null;
  reviewerName: string;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const review = useReviewCertificate();
  const toast = useToast();

  const submit = (decision: 'valid' | 'rejected') => {
    if (!certificate) return;
    if (decision === 'rejected' && !comment.trim()) {
      setError('A rejection needs a reason — the person is told why.');
      return;
    }
    setError(null);
    review.mutate(
      {
        id: certificate.id,
        decision,
        comment: comment.trim(),
        existing: certificate.comment,
        reviewerName,
      },
      {
        onSuccess: () => {
          toast.success(decision === 'valid' ? 'Certificate approved' : 'Certificate rejected');
          setComment('');
          onClose();
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const history = certificate?.comment ?? [];

  return (
    <Dialog open={Boolean(certificate)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h4">Review certificate</DialogTitle>
      <DialogContent>
        {certificate && (
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography variant="h4">{certificate.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {certificate.first_name} {certificate.last_name} · {certificate.badge_number} ·{' '}
                {certificate.section}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expires {dayjs(certificate.expiry_date).format('D MMM YYYY')}
              </Typography>
            </Stack>

            {history.length > 0 && (
              <>
                <Divider />
                <Stack gap={1}>
                  <Typography variant="overline" color="text.secondary">
                    Previous comments
                  </Typography>
                  {history.map((c, i) => (
                    <Stack key={i} gap={0.25}>
                      <Typography variant="body2">{c.comment}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.authorName ?? 'Unknown'} · {c.createdAt ?? ''}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Comment"
              helperText="Required when rejecting. The person sees this."
              multiline
              minRows={2}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={review.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => submit('rejected')}
          color="error"
          variant="outlined"
          disabled={review.isPending}
        >
          Reject
        </Button>
        <Button onClick={() => submit('valid')} variant="contained" disabled={review.isPending}>
          {review.isPending ? 'Saving…' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
